import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, X, CaretDown } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-61.4.1 — Async server-side searchable Buyer selector.
 *
 * Replaces the Iter-61.4 `<select>` client dropdown that was backed by
 * a preloaded 100-row page. In production there are 654+ clients — the
 * dropdown silently truncated ~85% of them, so admins could not select
 * or filter by Buyers outside the alphabetical first hundred.
 *
 * Behaviour:
 *   • Types Buyer name → debounced 300ms → `GET /organizations?type=client&q=&page_size=25`.
 *   • Focus with empty term → returns the first alphabetical page (25 rows).
 *   • Selecting a row calls `onChange({id, name})`.
 *   • The Clear (X) button clears the selection and — when `includeAll`
 *     is true — restores the "All buyers" sentinel (the filter mode).
 *   • Renders a read-only span in `disabled` mode (Edit destination —
 *     Iter-61.3 mandate).
 *
 * RBAC is preserved by relying on the existing `/organizations`
 * endpoint — the server enforces buyer/media_buyer/vendor 403 there.
 * Non-internal admins can NOT use this component to enumerate clients.
 */
export default function BuyerCombobox({
  value = null,                // {id, name} or null
  onChange,
  includeAll = false,          // filter mode — X clears back to "All buyers"
  allLabel = "All buyers",
  placeholder = "Search buyers…",
  disabled = false,
  testIdPrefix = "buyer-combo",
  initialName = null,          // fallback label when value.id is set but name unknown
}) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Resolve display name when parent passes value={id} without a name
  // (e.g. on Edit-load if the destination row is still fetching).
  const [resolvedName, setResolvedName] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!value?.id || value.name || initialName) { setResolvedName(null); return () => {}; }
    (async () => {
      try {
        const { data } = await api.get(`/organizations/${value.id}`);
        if (!cancelled) setResolvedName(data?.name || null);
      } catch {
        if (!cancelled) setResolvedName(null);
      }
    })();
    return () => { cancelled = true; };
  }, [value?.id, value?.name, initialName]);

  /**
   * Iter-62.1.3 — Stale-`initialName` hardening.
   *
   * `initialName` is a caller-provided fallback label for cases where
   * `value={id: ..., name: undefined}` (Edit-load before the parent's
   * async fetch resolves). It intentionally has no direct pairing to
   * `value.id` in the component API — but if a parent forgets to
   * refresh `initialName` alongside a fresh `value.id` (see the pre-
   * Iter-62.1.3 `DestinationForm` bug), the stale label would still
   * be rendered over a Buyer the user just switched away from.
   *
   * Guard: remember the (`initialName`, `value.id`) pair from the
   * previous render via a ref that's updated in a post-commit effect.
   * When `value.id` changes without `initialName` also changing in the
   * same render, `initialName` is stale and we suppress it — falling
   * through to `resolvedName` (which fetches the correct name for the
   * new id) or the placeholder.
   */
  const prevPairRef = useRef({ initialName, id: value?.id ?? null });
  const trustedInitialName = useMemo(() => {
    if (!initialName) return null;
    if (!value?.id) return null;
    const prev = prevPairRef.current;
    // First render or unchanged `value.id` — the pair holds.
    if (prev.id === value.id) return initialName;
    // `value.id` changed. Trust `initialName` only if the caller also
    // updated it in the same render — otherwise it's stale.
    if (prev.initialName !== initialName) return initialName;
    return null;
  }, [initialName, value?.id]);
  useEffect(() => {
    prevPairRef.current = { initialName, id: value?.id ?? null };
  }, [initialName, value?.id]);

  const displayName = value?.name || trustedInitialName || resolvedName || "";

  const runSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: "client",
        page_size: "25",
        sort: "name_asc",
      });
      const trimmed = (query || "").trim();
      if (trimmed) params.set("q", trimmed);
      const { data } = await api.get(`/organizations?${params.toString()}`);
      setRows(data.organizations || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce term → search.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [term, open, runSearch]);

  // Fetch initial page on open.
  useEffect(() => {
    if (open && rows.length === 0 && !term) runSearch("");
  }, [open, rows.length, term, runSearch]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (disabled) {
    return (
      <div
        className="w-full border border-black px-2 py-1.5 mt-1 bg-zinc-100 text-zinc-600 text-sm truncate"
        data-testid={`${testIdPrefix}-readonly`}
        title={displayName || "—"}
      >
        {displayName || "— platform-wide —"}
      </div>
    );
  }

  const selectRow = (row) => {
    onChange?.({ id: row.id, name: row.name });
    setOpen(false);
    setTerm("");
  };

  const clear = (e) => {
    e.stopPropagation();
    if (includeAll) onChange?.(null);
    else onChange?.({ id: "", name: "" });
    setTerm("");
    setRows([]);
  };

  const hasValue = !!value?.id;
  const chipLabel = hasValue ? displayName : (includeAll ? allLabel : placeholder);

  return (
    <div ref={wrapRef} className="relative w-full" data-testid={`${testIdPrefix}-root`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-trigger`}
        className="w-full border border-black px-2 py-1.5 text-sm text-left inline-flex items-center gap-1 hover:bg-zinc-50 min-h-[34px]"
      >
        <span className={`flex-1 truncate ${hasValue ? "" : "text-zinc-500"}`}>{chipLabel}</span>
        {hasValue && (
          <span
            role="button"
            tabIndex={0}
            onClick={clear}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && clear(e)}
            data-testid={`${testIdPrefix}-clear`}
            className="ml-1 border border-zinc-400 text-zinc-500 hover:bg-black hover:text-white px-1 py-0.5 leading-none inline-flex items-center"
            title="Clear"
          >
            <X size={10} />
          </span>
        )}
        <CaretDown size={12} className="text-zinc-500" />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 border-2 border-black bg-white z-40 max-h-64 overflow-y-auto brut-shadow-sm"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          <div className="p-2 border-b border-zinc-300 sticky top-0 bg-white">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                autoFocus
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Type a buyer name…"
                className="w-full border border-black pl-6 pr-2 py-1 text-sm"
                data-testid={`${testIdPrefix}-input`}
              />
            </div>
          </div>
          <div>
            {includeAll && (
              <button
                type="button"
                onClick={() => selectRow({ id: "", name: allLabel })}
                data-testid={`${testIdPrefix}-option-all`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-100 border-b border-zinc-200 italic text-zinc-600"
              >
                {allLabel}
              </button>
            )}
            {loading && (
              <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
                No buyers match "{term}"
              </div>
            )}
            {rows.map((r) => (
              <button
                type="button"
                key={r.id}
                onClick={() => selectRow(r)}
                data-testid={`${testIdPrefix}-option-${r.id}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-100 border-b border-zinc-100 truncate"
                title={r.name}
              >
                {r.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Convenience: normalise a value that might just be an id to
// `{id, name: ""}` so consumers don't have to guard.
export function toBuyerValue(id, name) {
  if (!id) return null;
  return { id, name: name || "" };
}
